import type { DataHealth, SourceHealth, SourceState } from '@ace-tracker/shared';
import { PLATFORM_LABELS, SOURCE_LABELS, timeAgo } from '../health';
import { DeveloperHealthSection } from './DeveloperHealthSection';
import { ExportMenu } from './ExportMenu';
import { InfoTooltip } from './InfoTooltip';
import { KpiCard } from './KpiCard';
import { ReadingGuide, type SectionHeadingProps } from './OptimizationShared';

const STATE: Record<SourceState, { label: string; icon: string; chip: string; meaning: string }> = {
  reporting: { label: 'Reporting', icon: 'fa-solid fa-circle-check', chip: 'bg-ok-bg text-ok-ink', meaning: 'Telemetry arrived from this source in the last 48 hours.' },
  stale: { label: 'Stopped reporting', icon: 'fa-solid fa-clock-rotate-left', chip: 'bg-warn-bg text-warn-ink', meaning: 'Devices reported before, but none in the last 48 hours.' },
  failing: { label: 'Failing', icon: 'fa-solid fa-triangle-exclamation', chip: 'bg-crit-bg text-crit-ink', meaning: "Every device's latest collector run for this source failed." },
  disabled: { label: 'Switched off', icon: 'fa-solid fa-toggle-off', chip: 'bg-surface-alt text-text-muted', meaning: 'Every device that reported a run has this source switched off in its configuration.' },
  'not-installed': { label: 'Not installed', icon: 'fa-solid fa-plug-circle-xmark', chip: 'bg-surface-alt text-text-muted', meaning: 'Collectors ran and found nothing to read for this source.' },
  'no-report': { label: 'No report', icon: 'fa-solid fa-circle-question', chip: 'bg-surface-alt text-text-muted', meaning: 'Nothing has been received, and no collector run says why.' },
};

const DASH = '—';

function percent(value: number | null): string {
  return value === null ? DASH : `${value.toFixed(1)}%`;
}

function minutes(value: number | null): string {
  if (value === null) return DASH;
  if (value < 1) return '<1 min';
  if (value < 90) return `${Math.round(value)} min`;
  return `${(value / 60).toFixed(1)} h`;
}

function When({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-text-faint">{DASH}</span>;
  return <span title={new Date(iso).toLocaleString()}>{timeAgo(iso)}</span>;
}

function StateChip({ state }: { state: SourceState }) {
  const style = STATE[state];
  return (
    <span title={style.meaning} className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-0.5 text-xs font-medium ${style.chip}`}>
      <i aria-hidden="true" className={style.icon} />
      {style.label}
    </span>
  );
}

function RunSummary({ source }: { source: SourceHealth }) {
  const runs = source.runs;
  if (!runs) return <span className="text-text-faint" title="No device on collector 1.4.1 or later has reported a run for this source.">{DASH}</span>;
  const parts = [
    runs.ok > 0 && `${runs.ok} ok`,
    runs.partial > 0 && `${runs.partial} partial`,
    runs.failed > 0 && `${runs.failed} failed`,
    runs.disabled > 0 && `${runs.disabled} off`,
    runs.notInstalled > 0 && `${runs.notInstalled} not installed`,
    runs.otherSkipped > 0 && `${runs.otherSkipped} nothing to send`,
  ].filter(Boolean);
  return <span title={`${runs.runs} runs from ${runs.devices} devices in this range. Each device is counted by its latest run.`}>{parts.join(' · ') || DASH}</span>;
}

/**
 * Data health: for every source, whether devices are reporting, how fresh
 * the telemetry is, and how collector runs ended — so a missing developer or
 * a stale figure can be explained. Pipeline facts only; no usage figures.
 */
export function DataHealthBody({ health, range, SectionHeading }: { health: DataHealth; range: { from: string; to: string }; SectionHeading: (props: SectionHeadingProps) => JSX.Element }) {
  const reportingSources = health.sources.filter((s) => s.state === 'reporting').length;
  const devices = health.devices;
  const devicePercent = devices.expected > 0 ? (devices.reporting / devices.expected) * 100 : null;
  const hasRuns = health.sources.some((s) => s.runs !== null);
  const { basisDays, windowHours } = health.deviceBasis;
  const expectedLabel = `devices seen in the prior ${basisDays} days`;
  const newest = health.collectorVersions[0]?.version;
  const behind = health.collectorVersions.slice(1).reduce((sum, v) => sum + v.devices, 0);
  const platformDevices = health.devicesByPlatform.reduce((sum, p) => sum + p.devices, 0);

  return (
    <>
      <ReadingGuide>
        {`This page describes the data pipeline, not usage. "Expected" devices are ${expectedLabel} — there is no managed device roster to compare against — and a device counts as reporting when it sent telemetry in the last ${windowHours} hours. Collector run reports (the Latest runs column) need collector 1.4.1 or later.`}
      </ReadingGuide>

      {!hasRuns && (
        <div role="status" className="rounded-card border border-border bg-surface px-4 py-3 text-sm text-text-muted">
          <p className="font-medium text-text-default">
            <i aria-hidden="true" className="fa-solid fa-circle-info mr-1.5 text-info" />
            No collector run reports yet
          </p>
          <p className="mt-1">
            No device in this range is on collector 1.4.1 or later. Until devices update, a source that sends nothing shows as "No report" or
            "Stopped reporting" — the dashboard cannot yet tell whether it is not installed or switched off.
          </p>
        </div>
      )}

      <section>
        <SectionHeading title="At a glance" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard label="Sources reporting" value={`${reportingSources} of ${health.sources.length}`} meta={`Telemetry in the last ${windowHours} hours`} />
          <KpiCard
            label="Device coverage"
            value={devicePercent === null ? DASH : `${Math.round(devicePercent)}%`}
            meta={`${devices.reporting} of ${devices.expected} ${expectedLabel} sent something`}
            accentClassName={devicePercent === null ? 'bg-primary-blue' : devicePercent >= 85 ? 'bg-success' : devicePercent >= 60 ? 'bg-warning' : 'bg-error'}
          />
          <KpiCard
            label="Data as of"
            value={health.dataAsOf ? timeAgo(health.dataAsOf) : DASH}
            meta={health.dataAsOf ? `${new Date(health.dataAsOf).toLocaleString()} · latest AI tool telemetry in range` : 'No AI tool telemetry in this range'}
          />
          <KpiCard
            label="Collector versions"
            value={String(health.collectorVersions.length)}
            meta={newest ? (behind > 0 ? `Newest ${newest} · ${behind} ${behind === 1 ? 'device' : 'devices'} on an older version` : `All devices on ${newest}`) : 'No version reported'}
          />
        </div>
      </section>

      <section>
        <SectionHeading title="Sources" hint="One row per telemetry source, for the selected range" />
        <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
          <ExportMenu title="Data health sources" className="-mr-2 -mt-2 mb-1 justify-end" />
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] text-left text-sm">
              <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                <tr>
                  <th scope="col" className="pb-2.5 pr-3">Source</th>
                  <th scope="col" className="pb-2.5 pr-3">State</th>
                  <th scope="col" className="pb-2.5 pr-3 text-right">
                    <span className="inline-flex items-center gap-1">Devices <InfoTooltip text={`Reporting in the last ${windowHours} hours, out of ${expectedLabel}.`} /></span>
                  </th>
                  <th scope="col" className="pb-2.5 pr-3 text-right">Last telemetry</th>
                  <th scope="col" className="pb-2.5 pr-3 text-right">
                    <span className="inline-flex items-center gap-1">Ingestion lag <InfoTooltip text="Time from an event being built on the device to it reaching the workspace: median, then 95th percentile." /></span>
                  </th>
                  <th scope="col" className="pb-2.5 pr-3">
                    <span className="inline-flex items-center gap-1">Latest runs <InfoTooltip text="Devices by the outcome of their latest collector run. Partial means ingestion accepted only some of what was sent." /></span>
                  </th>
                  <th scope="col" className="pb-2.5 pr-3 text-right">
                    <span className="inline-flex items-center gap-1">Rejected <InfoTooltip text="Items ingestion permanently rejected, across all runs in the range. They are quarantined on the device and not resent." /></span>
                  </th>
                  <th scope="col" className="pb-2.5 pr-3 text-right">
                    <span className="inline-flex items-center gap-1">Duplicates <InfoTooltip text="Share of events repeating an identifier already received. Dashboard figures de-duplicate these." /></span>
                  </th>
                  <th scope="col" className="pb-2.5 pr-3 text-right">
                    <span className="inline-flex items-center gap-1">No identifier <InfoTooltip text="Share of events with no developer or no device identifier." /></span>
                  </th>
                  <th scope="col" className="pb-2.5 text-right">
                    <span className="inline-flex items-center gap-1">Fallback cost <InfoTooltip text="Share of token cost priced at the fallback rate because the model has no price in Settings. Claude Code and Codex only." /></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {health.sources.map((source) => (
                  <tr key={source.source} className="border-b border-border last:border-0">
                    <th scope="row" className="py-2.5 pr-3 font-medium text-text-default">{SOURCE_LABELS[source.source]}</th>
                    <td className="py-2.5 pr-3"><StateChip state={source.state} /></td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{source.devices.reporting} of {source.devices.expected}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-text-muted"><When iso={source.lastObserved} /></td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-text-muted">
                      {source.lagMinutes.p50 === null ? DASH : `${minutes(source.lagMinutes.p50)} / ${minutes(source.lagMinutes.p95)}`}
                    </td>
                    <td className="py-2.5 pr-3 text-text-muted"><RunSummary source={source} /></td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{source.runs ? source.runs.itemsRejected.toLocaleString() : DASH}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{percent(source.duplicatePercent)}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{percent(source.missingIdentifierPercent)}</td>
                    <td className="py-2.5 text-right tabular-nums">{percent(source.fallbackCostPercent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-text-faint">A dash means not known or not applicable, never zero.</p>
        </div>
      </section>

      <DeveloperHealthSection range={range} SectionHeading={SectionHeading} />

      <section className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="space-y-5">
          <div>
            <SectionHeading title="Collector versions" hint="Devices by the version they last reported" />
            <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
              <ExportMenu title="Collector versions" className="-mr-2 -mt-2 mb-1 justify-end" />
              {health.collectorVersions.length === 0 ? (
                <p className="text-sm text-text-muted">No device reported a collector version in this range.</p>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                    <tr>
                      <th scope="col" className="pb-2.5 pr-3">Version</th>
                      <th scope="col" className="pb-2.5 text-right">Devices</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.collectorVersions.map((v, i) => (
                      <tr key={v.version} className="border-b border-border last:border-0">
                        <td className="py-2.5 pr-3 font-mono text-xs text-text-default">
                          {v.version}
                          {i === 0 && <span className="ml-2 rounded-pill bg-surface-alt px-2 py-0.5 font-sans text-[11px] text-text-muted">newest seen</span>}
                        </td>
                        <td className="py-2.5 text-right tabular-nums">{v.devices}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div>
            <SectionHeading title="Devices by platform" hint="Devices by the platform their latest collector run reported" />
            <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
              <ExportMenu title="Devices by platform" className="-mr-2 -mt-2 mb-1 justify-end" />
              {health.devicesByPlatform.length === 0 ? (
                <p className="text-sm text-text-muted">No collector run reported a platform in this range. Platform needs collector 1.4.1 or later.</p>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                    <tr>
                      <th scope="col" className="pb-2.5 pr-3">Platform</th>
                      <th scope="col" className="pb-2.5 pr-3 text-right">Devices</th>
                      <th scope="col" className="pb-2.5 text-right">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.devicesByPlatform.map((p) => (
                      <tr key={p.platform} className="border-b border-border last:border-0">
                        <th scope="row" className="py-2.5 pr-3 font-medium text-text-default">
                          {p.platform === 'unknown' ? (
                            <span className="inline-flex items-center gap-1">
                              {PLATFORM_LABELS.unknown} <InfoTooltip text="The collector run carried no platform, or one the dashboard does not recognise." />
                            </span>
                          ) : (
                            PLATFORM_LABELS[p.platform]
                          )}
                        </th>
                        <td className="py-2.5 pr-3 text-right tabular-nums">{p.devices}</td>
                        <td className="py-2.5 text-right tabular-nums text-text-muted">{Math.round((p.devices / platformDevices) * 100)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {health.devicesByPlatform.length > 0 && <p className="mt-3 text-xs text-text-faint">Counts only devices on collector 1.4.1 or later; older collectors do not report a platform.</p>}
            </div>
          </div>
        </div>

        <div>
          <SectionHeading title="What each state means" />
          <div className="rounded-card border border-border bg-surface p-5 shadow-sm">
            <dl className="space-y-2.5 text-sm">
              {(Object.keys(STATE) as SourceState[]).map((state) => (
                <div key={state} className="grid grid-cols-[10.5rem_1fr] items-start gap-3">
                  <dt><StateChip state={state} /></dt>
                  <dd className="text-text-muted">{STATE[state].meaning}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <footer className="flex flex-wrap gap-3.5 border-t border-border pt-4 text-xs text-text-faint">
        <span>Queried {timeAgo(health.queriedAt)}</span>
        <span>Excluded developers' devices still count toward coverage</span>
        <span>Parse failures are shown on the RTK page</span>
      </footer>
    </>
  );
}
